'use client';

import { Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  AuthOtpResendButton,
  OtpInput,
  TrackedButton,
  ValidatedPhoneInput,
} from '@/components';
import { useFooter } from '@/contexts';
import { AuthMachineEvent } from '@/machines';
import { logger } from '@/utils';

interface OtpVerificationFormProps {
  send: (event: AuthMachineEvent) => void;
  phoneNumber: string;
  otpCode: string;
  flowId: string | null;
  csrfToken: string | null;
  isLoading?: boolean;
  onRetry?: () => void;
}

const OtpVerificationForm = ({
  send,
  phoneNumber,
  otpCode,
  flowId,
  csrfToken,
  isLoading = false,
}: OtpVerificationFormProps) => {
  const buttonText = useMemo(() => 'VERIFY', []);

  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm({
    initialValues: {
      phoneNumber,
      otpCode,
    },
    validate: {
      phoneNumber: (value) => {
        if (!value) return 'Phone number is required';

        // E.164 format check for US numbers (+1XXXXXXXXXX)
        if (!/^\+1[0-9]{10}$/.test(value.replace(/\s+/g, ''))) {
          return 'Enter a valid phone number';
        }
        return null;
      },
      otpCode: (value) => {
        if (!value) return 'OTP is required';

        if (!/^\[0-9]{6}$/.test(value.replace(/\s+/g, '')))
          return 'OTP must be six digits';
      },
    },
  });

  const handlePhoneInputFocus = () => {
    send({
      type: 'BACK',
    });
  };

  const handleVerifyOtp = useCallback(() => {
    logger.debug(`Requesting OTP verification with code: ${otpCode}`);

    if (!flowId || !csrfToken) {
      logger.error('Missing flowId or csrfToken for OTP verification');
      send({
        type: 'FLOW_ERROR',
        message: 'Session expired. Please try again.',
      });
      return;
    }

    // Submit the OTP to the state machine for verification
    send({
      type: 'OTP_SUBMITTED',
    });
  }, [csrfToken, otpCode, flowId, send]);

  const handleSubmit = useCallback(async () => {
    if (otpCode?.length === 6) {
      handleVerifyOtp();
    }
  }, [otpCode, handleVerifyOtp]);

  const handleOtpChange = (value: string) => {
    send({
      type: 'UPDATE_OTP',
      otpCode: value,
    });
  };

  const handleOtpError = (message: string) => {
    send({
      type: 'FLOW_ERROR',
      message,
    });
  };

  const { buttonPrimaryProps, setButtonPrimaryProps, clearButtonPrimaryProps } =
    useFooter();

  useEffect(() => {
    setButtonPrimaryProps({
      children: buttonText,
      type: 'submit',
      loading: isLoading,
      disabled: otpCode?.length !== 6,
      variant: 'primary',
      trackingName: 'verify_otp',
      onClick: () => formRef?.current?.requestSubmit(),
    });

    return () => clearButtonPrimaryProps();
  }, [
    buttonText,
    clearButtonPrimaryProps,
    handleSubmit,
    isLoading,
    otpCode?.length,
    setButtonPrimaryProps,
  ]);

  return (
    <form
      ref={formRef}
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <Stack gap="lg">
        <ValidatedPhoneInput
          {...form.getInputProps('phoneNumber')}
          value={form.values.phoneNumber}
          onFocus={handlePhoneInputFocus}
          error={form.errors.phoneNumber}
          placeholder="(123) 456-7890"
          required
          disabled={isLoading}
        />

        <OtpInput
          phoneNumber={form.values.phoneNumber}
          otpCode={form.values.otpCode}
          onChange={handleOtpChange}
          disabled={isLoading}
          onSubmit={handleVerifyOtp}
          autoFocus
        />

        <Stack gap="xs">
          <TrackedButton
            fullWidth
            mt="lg"
            size="lg"
            visibleFrom="md"
            {...buttonPrimaryProps}
          />

          <AuthOtpResendButton
            phoneNumber={form.values.phoneNumber}
            state="awaitingOtpInput"
            send={send}
            disabled={isLoading}
            onError={handleOtpError}
            cooldownSeconds={30}
          />
        </Stack>
      </Stack>
    </form>
  );
};

export { OtpVerificationForm };
